import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import "./Privacy.scss";
import DynamicHeader from "../../Header/DynamicHeader";

const Privacy = () => {
  const SUPPORT_EMAIL =
    process.env.REACT_APP_SUPPORT_EMAIL || "support@cvanto.com";

  const PUBLIC_BASE_URL =
    process.env.REACT_APP_PUBLIC_BASE_URL || "https://cvanto.com";

  const PRIVACY_POLICY_URL =
    process.env.REACT_APP_PRIVACY_POLICY_URL || "https://cvanto.com/privacy";

  const rootRef = useRef(null);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    const items = Array.from(rootEl.querySelectorAll(".js-reveal"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            observer.unobserve(e.target);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px 0px -10% 0px",
        threshold: 0.1,
      }
    );

    items.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Політика конфіденційності — Cvanto</title>
        <meta name="description" content="Політика конфіденційності маркетплейсу Cvanto." />
        <link rel="canonical" href={PRIVACY_POLICY_URL} />
      </Helmet>

      <DynamicHeader
        title="Політика конфіденційності"
        variant="glass"
        size="regular"
        showBack
        withSpacer
      />

      <div className="legal legal--privacy" ref={rootRef}>
        <header className="legal__hero js-reveal">
          <div className="legal__hero-body">
            <h1 className="legal__title">Політика конфіденційності Cvanto</h1>

            <p className="legal__subtitle">
              Платформа: <u>{PUBLIC_BASE_URL}</u>
            </p>

            <p className="legal__subtitle">
              URL політики:{" "}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
                {PRIVACY_POLICY_URL}
              </a>
            </p>

            <p className="legal__subtitle">
              Останнє оновлення: <b>14 червня 2026 року</b>
            </p>
          </div>
        </header>

        <nav className="legal__toc js-reveal" aria-label="Зміст">
          <strong>Зміст</strong>

          <ol>
            <li><a href="#scope">1. Область застосування</a></li>
            <li><a href="#principles">2. Принципи конфіденційності</a></li>
            <li><a href="#legalbasis">3. Правові підстави обробки</a></li>
            <li><a href="#data">4. Дані, які ми збираємо</a></li>
            <li><a href="#account">5. Акаунт та видалення даних</a></li>
            <li><a href="/delete-account">Як видалити акаунт?</a></li>
            <li><a href="#content">6. Контент користувачів</a></li>
            <li><a href="#messages">7. Повідомлення та чати</a></li>
            <li><a href="#push">8. Push-сповіщення</a></li>
            <li><a href="#payments">9. Платежі та доставки</a></li>
            <li><a href="#ai">10. AI/ML-функції</a></li>
            <li><a href="#cookies">11. Cookies, LocalStorage та ідентифікатори</a></li>
            <li><a href="#use">12. Як ми використовуємо дані</a></li>
            <li><a href="#sharing">13. Обмін інформацією</a></li>
            <li><a href="#xborder">14. Транскордонні передачі</a></li>
            <li><a href="#retention">15. Строки зберігання</a></li>
            <li><a href="#security">16. Безпека</a></li>
            <li><a href="#children">17. Діти та підлітки</a></li>
            <li><a href="#ads">18. Маркетинг, аналітика та реклама</a></li>
            <li><a href="#rights">19. Ваші права</a></li>
            <li><a href="#law">20. Запити державних органів</a></li>
            <li><a href="#changes">21. Зміни до Політики</a></li>
            <li><a href="#contacts">22. Контакти</a></li>
          </ol>
        </nav>

        <section id="scope" className="legal__section js-reveal">
          <h2>1. Область застосування</h2>
          <p>
            Ця Політика конфіденційності пояснює, як Cvanto збирає,
            використовує, зберігає, передає та захищає персональні дані
            користувачів під час користування мобільним застосунком, вебсайтом
            та пов’язаними сервісами Cvanto.
          </p>
        </section>

        <section id="principles" className="legal__section js-reveal">
          <h2>2. Принципи конфіденційності</h2>
          <p>
            Cvanto збирає лише ті дані, які потрібні для роботи платформи,
            безпеки, підтримки користувачів, покращення сервісу та виконання
            юридичних обов’язків. Ми не продаємо персональні дані користувачів
            третім сторонам.
          </p>
        </section>

        <section id="legalbasis" className="legal__section js-reveal">
          <h2>3. Правові підстави обробки</h2>
          <p>
            Ми обробляємо персональні дані для надання сервісу, виконання
            користувацької угоди, захисту законних інтересів Cvanto,
            забезпечення безпеки, виконання юридичних обов’язків або на
            підставі згоди користувача, якщо така згода потрібна.
          </p>
        </section>

        <section id="data" className="legal__section js-reveal">
          <h2>4. Дані, які ми збираємо</h2>
          <p>
            Cvanto може обробляти такі дані: email, номер телефону, ім’я
            користувача, пароль у захищеній формі, аватар, місто, опис профілю,
            тип профілю, дату народження, налаштування мови й теми, оголошення,
            фото, відео, пости, збережені елементи, підписки, скарги,
            блокування, повідомлення, технічні події, IP-адресу, дані пристрою,
            push-токени, дані безпеки та іншу інформацію, яку користувач
            самостійно надає в межах сервісу.
          </p>
        </section>

        <section id="account" className="legal__section js-reveal">
          <h2>5. Акаунт та видалення даних</h2>
          <p>
            Користувач може створити акаунт для доступу до функцій Cvanto.
            Користувач може самостійно видалити акаунт у налаштуваннях
            застосунку. Після видалення акаунта персональні дані видаляються
            або анонімізуються, крім випадків, коли їх зберігання необхідне для
            безпеки, запобігання шахрайству, виконання юридичних обов’язків або
            вирішення спорів.
          </p>

          <a className="legal__button" href="/delete-account">
            Як видалити акаунт?
          </a>
        </section>

        <section id="content" className="legal__section js-reveal">
          <h2>6. Контент користувачів</h2>
          <p>
            Користувачі можуть створювати оголошення, пости, профілі, описи,
            фотографії, відео та інший контент. Частина такого контенту може
            бути публічно доступною іншим користувачам Cvanto залежно від
            функції, у якій його опубліковано.
          </p>
        </section>

        <section id="messages" className="legal__section js-reveal">
          <h2>7. Повідомлення та чати</h2>
          <p>
            Cvanto надає функції обміну повідомленнями між користувачами. Дані
            чатів використовуються для доставки повідомлень, відображення
            історії листування, забезпечення безпеки, розгляду скарг та
            підтримки користувачів. Приватні чати не продаються третім сторонам
            і не передаються рекламодавцям для таргетованої реклами.
          </p>
        </section>

        <section id="push" className="legal__section js-reveal">
          <h2>8. Push-сповіщення</h2>
          <p>
            Cvanto може надсилати push-сповіщення про повідомлення, реакції,
            взаємодії з оголошеннями, оновлення сервісу, безпеку акаунта та
            інші важливі події. Для цього ми можемо обробляти push-токен
            пристрою. Користувач може керувати push-сповіщеннями в
            налаштуваннях пристрою або застосунку.
          </p>
        </section>

        <section id="payments" className="legal__section js-reveal">
          <h2>9. Платежі та доставки</h2>
          <p>
            Якщо в Cvanto доступні платні функції, платежі можуть оброблятися
            сторонніми платіжними сервісами. Cvanto не зберігає повні дані
            банківських карток. Ми можемо отримувати лише технічні метадані
            транзакції, статус платежу, суму, валюту та ідентифікатор операції.
          </p>
        </section>

        <section id="ai" className="legal__section js-reveal">
          <h2>10. AI/ML-функції</h2>
          <p>
            Cvanto може використовувати алгоритми, автоматизовані системи або
            AI/ML-функції для категоризації оголошень, рекомендацій,
            сортування, пошуку, модерації, виявлення шахрайства та покращення
            користувацького досвіду.
          </p>
        </section>

        <section id="cookies" className="legal__section js-reveal">
          <h2>11. Cookies, LocalStorage та ідентифікатори</h2>
          <p>
            Cvanto може використовувати cookies, LocalStorage, мобільні
            ідентифікатори, токени авторизації та інші технічні засоби для
            входу в акаунт, безпеки, збереження налаштувань, стабільної роботи
            застосунку, аналітики та покращення сервісу.
          </p>
        </section>

        <section id="use" className="legal__section js-reveal">
          <h2>12. Як ми використовуємо дані</h2>
          <p>
            Дані використовуються для роботи Cvanto: створення і керування
            акаунтами, профілями, оголошеннями, чатами, пошуком, фільтрами,
            сповіщеннями, модерацією, підтримкою, безпекою, персоналізацією,
            аналітикою, покращенням продукту та виконанням юридичних
            обов’язків.
          </p>
        </section>

        <section id="sharing" className="legal__section js-reveal">
          <h2>13. Обмін інформацією</h2>
          <p>
            Cvanto може передавати дані постачальникам послуг, які допомагають
            забезпечувати роботу платформи: хостингу, хмарній інфраструктурі,
            медіасховищу, аналітиці, email/SMS-сервісам, push-сервісам,
            платіжним сервісам, модерації, безпеці та підтримці. Такі
            постачальники мають доступ лише до даних, необхідних для виконання
            своїх функцій.
          </p>
        </section>

        <section id="xborder" className="legal__section js-reveal">
          <h2>14. Транскордонні передачі</h2>
          <p>
            Дані можуть оброблятися або зберігатися на серверах за межами
            країни користувача. У таких випадках Cvanto застосовує відповідні
            технічні, організаційні та правові заходи захисту.
          </p>
        </section>

        <section id="retention" className="legal__section js-reveal">
          <h2>15. Строки зберігання</h2>
          <p>
            Дані зберігаються стільки, скільки потрібно для роботи сервісу,
            виконання юридичних обов’язків, безпеки, запобігання шахрайству,
            підтримки користувачів або вирішення спорів. Технічні метадані та
            журнали безпеки можуть зберігатися до 12 місяців або довше, якщо це
            необхідно за законом.
          </p>
        </section>

        <section id="security" className="legal__section js-reveal">
          <h2>16. Безпека</h2>
          <p>
            Ми застосовуємо шифрування під час передачі даних, контроль
            доступу, моніторинг подій, інфраструктурні заходи безпеки та інші
            технічні засоби захисту. Водночас жоден цифровий сервіс не може
            гарантувати абсолютну безпеку.
          </p>
        </section>

        <section id="children" className="legal__section js-reveal">
          <h2>17. Діти та підлітки</h2>
          <p>
            Cvanto не призначено для дітей молодше 16 років. Якщо ми дізнаємося,
            що акаунт створено особою молодше 16 років без належної згоди, ми
            можемо обмежити функціонал, видалити контент або видалити акаунт.
          </p>
        </section>

        <section id="ads" className="legal__section js-reveal">
          <h2>18. Маркетинг, аналітика та реклама</h2>
          <p>
            Cvanto може використовувати внутрішню аналітику, промо-інструменти
            та рекламні можливості для розвитку платформи, показу релевантного
            контенту, вимірювання ефективності функцій і покращення сервісу.
            Приватні повідомлення не використовуються для продажу таргетованої
            реклами третім сторонам.
          </p>
        </section>

        <section id="rights" className="legal__section js-reveal">
          <h2>19. Ваші права</h2>
          <p>
            Користувач може звернутися до Cvanto із запитом на доступ до своїх
            даних, виправлення, видалення, обмеження обробки, заперечення проти
            обробки або перенесення даних, якщо такі права застосовуються
            відповідно до законодавства.
          </p>
        </section>

        <section id="law" className="legal__section js-reveal">
          <h2>20. Запити державних органів</h2>
          <p>
            Cvanto може розкривати інформацію на офіційні, належним чином
            оформлені запити компетентних державних органів, якщо це
            вимагається законом або необхідно для захисту прав, безпеки
            користувачів чи платформи.
          </p>
        </section>

        <section id="changes" className="legal__section js-reveal">
          <h2>21. Зміни до Політики</h2>
          <p>
            Cvanto може періодично оновлювати цю Політику. Нова редакція
            набирає чинності з моменту публікації в застосунку або на сайті,
            якщо інше не зазначено окремо. У разі суттєвих змін ми можемо
            повідомити користувачів додатково.
          </p>
        </section>

        <section id="contacts" className="legal__section js-reveal">
          <h2>22. Контакти</h2>
          <p>
            З питань конфіденційності, видалення даних, доступу до даних або
            роботи Cvanto звертайтесь на:{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </section>
      </div>
    </>
  );
};

export default Privacy;